import React from 'react'
import { Box, Container, Grid, Typography, Stack, Button, useTheme, alpha } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Navigation from './Navigation'
import { useColorMode } from '../theme'

interface LayoutProps {
    children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const theme = useTheme()
    const { t } = useTranslation()
    const { mode } = useColorMode()

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: mode === 'dark'
                ? `linear-gradient(135deg, ${alpha('#1a1a1a', 0.95)} 0%, ${alpha('#2d2d2d', 0.95)} 100%)`
                : `linear-gradient(135deg, ${alpha('#f8f9ff', 0.95)} 0%, ${alpha('#f0f2ff', 0.95)} 100%)`,
            overflowX: 'hidden',
            width: '100%',
            maxWidth: '100vw'
        }}>
            {/* Navigation */}
            <Navigation />

            {/* Main Content */}
            <Box sx={{
                flex: 1,
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden'
            }}>
                {children}
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    bgcolor: mode === 'dark' ? alpha('#1a1a1a', 0.8) : alpha('#f8f9ff', 0.8),
                    py: { xs: 2, sm: 3 },
                    mt: 'auto',
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    width: '100%',
                    maxWidth: '100%'
                }}
            >
                <Container maxWidth="lg" sx={{
                    px: { xs: 1, sm: 2 },
                    width: '100%',
                    maxWidth: '100%'
                }}>
                    <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                            >
                                {t('copyright')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack
                                direction="row"
                                spacing={{ xs: 1, sm: 2 }}
                                justifyContent={{ xs: 'center', md: 'flex-end' }}
                                sx={{ mt: { xs: 1, md: 0 } }}
                            >
                                <Button
                                    size="small"
                                    href="/impressum"
                                    sx={{
                                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                        minWidth: 'auto',
                                        px: { xs: 1, sm: 2 },
                                        '&:hover': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                >
                                    {t('imprint')}
                                </Button>
                                <Button
                                    size="small"
                                    href="/datenschutz"
                                    sx={{
                                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                        minWidth: 'auto',
                                        px: { xs: 1, sm: 2 },
                                        '&:hover': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                >
                                    {t('privacy')}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    )
}

export default Layout
